import { ReactNode, useEffect, useMemo, useState, useContext } from 'react';
import {
  DarwinFlex,
  DarwinLayout,
  DarwinSpin,
  DMenuProps,
} from 'components/darwin-ui';

import { useLocation } from 'react-router-dom';
import { Enroll } from 'pages/Enroll';
import { WelcomeHome } from 'pages/welcomeHome/WelcomeHome';
import { AuthContext } from 'contexts/AuthContext';
import { useWeb3 } from 'contexts/Web3Context';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { AppHeader } from './LayoutComponents/Header/AppHeader';
import { SideMenu } from './LayoutComponents/SideBar/SideMenu';
import {
  useGetLoggedUser,
  useGetUserCompanies,
  useGetUserPermissionsPerCompany,
} from 'api/services/userService';
import { useUI } from 'contexts/UiContext';
import { useTranslation } from 'react-i18next';
import styles from './Layout.module.css';
import { isDummyLogin } from 'services/api/utils';

const { Sider, Content } = DarwinLayout;
export type MenuItem = DMenuProps['items'];

interface Props {
  children: ReactNode;
}

const AppLayout = ({ children }: Props) => {
  const location = useLocation();
  const dummyLogin = isDummyLogin();

  const { address, signMessage } = useWeb3();
  const { login, loginWithEmail, logout, isAuthenticated } =
    useContext(AuthContext);

  const hasAddress = dummyLogin || Boolean(address);
  const isEffectivelyAuth = hasAddress && isAuthenticated;

  const { openNotification } = useFloatNotification();
  const { t } = useTranslation();
  const {
    selectedCompany,
    setSelectedCompany,
    setUserPermissionsPerCompany,
    setUserPrimaryCompany,
  } = useUI();

  const [collapsed, setCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    data: companiesData,
    isLoading: companiesIsLoading,
    error: companiesError,
  } = useGetUserCompanies({ enabled: isEffectivelyAuth });

  const companies = useMemo(
    () => companiesData?.data.companies ?? [],
    [companiesData]
  );

  const { data: userPermissionsData } = useGetUserPermissionsPerCompany(
    selectedCompany ?? '',
    { enabled: isEffectivelyAuth && Boolean(selectedCompany) }
  );

  const {
    data: userData,
    isLoading: userDataIsLoading,
    error: userError,
  } = useGetLoggedUser({ enabled: isEffectivelyAuth });

  useEffect(() => {
    if (companiesError?.message) {
      openNotification('Error', t('layout.connection_error'), 'error');
    }
  }, [companiesError, openNotification, t]);

  useEffect(() => {
    if (isEffectivelyAuth && userPermissionsData) {
      setUserPermissionsPerCompany(userPermissionsData.data);
    }
  }, [isEffectivelyAuth, userPermissionsData, setUserPermissionsPerCompany]);

  useEffect(() => {
    if (companies.length) {
      if (
        !selectedCompany ||
        !companies.some(c => c.id.toString() === selectedCompany)
      ) {
        setSelectedCompany(companies[0].id.toString());
      }
      setUserPrimaryCompany(companies.find(c => c.isPrimary));
    }
  }, [companies, selectedCompany, setSelectedCompany, setUserPrimaryCompany]);

  useEffect(() => {
    if (userError?.message) {
      openNotification('Error', t('layout.no_user_contact_support'), 'error');
    }
  }, [userError, openNotification, t]);

  useEffect(() => {
    /* dummy-login */
    if (dummyLogin) {
      const cachedEmail = localStorage.getItem('darwin_user_email');
      if (cachedEmail && !isAuthenticated) {
        (async () => {
          setIsLoading(true);
          try {
            await loginWithEmail(cachedEmail);
          } catch (e: any) {
            openNotification('Login', e.message, 'error');
          } finally {
            setIsLoading(false);
          }
        })();
      }
      return; // skip wallet flow
    }

    /* real wallet flow */
    if (address && !isAuthenticated) {
      (async () => {
        setIsLoading(true);
        try {
          const nonce =
            'login: ' + Math.random() * (10000000000 - 10000) + 10000;
          const sig = await signMessage(nonce);
          await login(nonce, sig);
        } catch (e: any) {
          openNotification('Login', e.message, 'error');
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [
    dummyLogin,
    address,
    isAuthenticated,
    login,
    loginWithEmail,
    openNotification,
    signMessage,
  ]);

  if (location.pathname === '/enroll-now') return <Enroll />;
  if (!isEffectivelyAuth) return <WelcomeHome />;

  return (
    <DarwinFlex gap="middle" wrap="wrap">
      <DarwinSpin
        spinning={isLoading || userDataIsLoading || companiesIsLoading}
        fullscreen
      />
      <DarwinLayout style={{ width: '100%' }}>
        <AppHeader
          handleDisconnect={logout}
          companies={companies}
          userData={userData?.data}
        />
        <DarwinLayout>
          {hasAddress && location.pathname !== '/admin' && (
            <Sider
              collapsible
              style={{
                textAlign: 'center',
                lineHeight: '120px',
                color: '#808191',
                backgroundColor: '#22252C',
                boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.50)',
              }}
              collapsed={collapsed}
              onCollapse={setCollapsed}
            >
              <SideMenu />
            </Sider>
          )}
          <Content className={styles.content}>{children}</Content>
        </DarwinLayout>
      </DarwinLayout>
    </DarwinFlex>
  );
};

export default AppLayout;
